/**
 * Edit user: profile fields, role, verification, and account active (admin PATCH).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import adminApi from '../../services/adminApi';
import type { UserWithPermissionsItem } from '../../types';

export interface AdminUserEditDialogProps {
  open: boolean;
  user: UserWithPermissionsItem | null;
  currentUserId: number;
  onClose: () => void;
  onSaved: (updated: UserWithPermissionsItem) => void;
}

const AdminUserEditDialog: React.FC<AdminUserEditDialogProps> = ({
  open,
  user,
  currentUserId,
  onClose,
  onSaved,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState<'user' | 'admin' | 'superuser'>('user');
  const [isActive, setIsActive] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setFirstName(user.first_name ?? '');
      setLastName(user.last_name ?? '');
      setCompany(user.company ?? '');
      setRole((user.role as 'user' | 'admin' | 'superuser') || 'user');
      setIsActive(user.is_active);
      setIsVerified(user.is_verified);
      setError(null);
    }
  }, [open, user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminApi.patchAdminUser(user.id, {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        company: company.trim() || null,
        is_active: isActive,
        is_verified: isVerified,
        role,
      });
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      console.error(e);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const isSelf = user?.id === currentUserId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit user</DialogTitle>
      <DialogContent>
        {user && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {user.email}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="edit-user-role">Role</InputLabel>
            <Select
              labelId="edit-user-role"
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'user' | 'admin' | 'superuser')}
            >
              <MenuItem value="user">user</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
              <MenuItem value="superuser">superuser</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isVerified}
                onChange={(_, v) => setIsVerified(v)}
                disabled={!user}
              />
            }
            label="Email verified"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(_, v) => setIsActive(v)}
                disabled={!user || isSelf}
              />
            }
            label="Account active"
          />
          {isSelf && (
            <Typography variant="caption" color="text.secondary">
              You cannot deactivate your own account from here.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !user}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminUserEditDialog;
